/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect } from "react";

export type Language = "fa" | "en";

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  isRtl: boolean;
  t: (key: keyof typeof translations["fa"]) => string;
  localized: (path: string) => string;
}

const translations = {
  fa: {
    // Header
    brandName: "راز و روان",
    brandSubtitle: "RazoRavan",
    navHome: "تالار اصلی",
    navVideos: "ویدئو ها",
    navArticles: "کتیبه ها",
    navQuiz: "خودشناسی",
    navHaftKhan: "هفت خوان",
    deepReflectionActive: "خلوتگاه: فعال",
    deepReflectionInactive: "خلوتگاه",
    muteSound: "خاموش کردن هم‌آوای درونی",
    unmuteSound: "شنیدن هم‌آوای درونی (۵۲۸ هرتز)",
    
    // Hero
    heroTitle: "پورتال راز و روان",
    heroSubtitle: "اسطوره‌های ایران، از نگاه روان‌شناختی",
    heroDescription: "اینجا، اسطوره تنها روایت گذشته نیست؛\nبلکه زبانی برای شناخت روان، کشف کهن‌الگوها و آشتی با خویشتن است.",
    archetypeTitle: "— نقشه کهن‌الگوهای روان —",
    epicAdaptation: "تطبیق حماسی:",
    jungianPerspective: "دیدگاه یونگی:",
    shahnamehSymbol: "نماد شاهنامه",
    discoverArticles: "مطالعه این کهن‌الگو",
    
    // YouTube Feed
    videoTitle: "ویدئوهای برگزیده راز و روان",
    videoSubtitle: "منتخبی از عمیق‌ترین روایت‌های اسطوره، روان و کهن‌الگوها؛\nبرای آنان که می‌خواهند فراتر از ظاهر داستان‌ها را ببینند.",
    searchVideos: "جستجو در ویدیوها...",
    viewAllVideos: "ورود به آرشیو ویدئوها",
    duration: "مدت زمان:",
    publishDate: "تاریخ انتشار:",
    watchOnYoutube: "جهت تماشای این ویدیو در محیط یوتیوب روی گزینه‌های روبرو کلیک کنید:",
    watchOnYoutubeBtn: "تماشا در برنامه یوتیوب",
    shareVideo: "اشتراک‌گذاری ویدیو",
    copyLink: "کپی لینک",
    close: "بستن",
    linkCopied: "لینک ویدیو با موفقیت کپی شد!",
    
    // YouTube Connection Settings (Admin)
    ytSettings: "تنظیمات اتصال یوتیوب",
    ytSettingsSubtitle: "بخش مدیریت و پیکربندی اختصاصی برای مدیر سایت (دسترسی لوکال)",
    channelIdLabel: "شناسه کانال یوتیوب (نام کاربری یا آی‌دی با شروع از UC):",
    apiKeyLabel: "کلید YouTube API v3 گوگل:",
    statusLabel: "وضعیت اتصال:",
    statusConnected: "متصل به کانال واقعی یوتیوب (نمایش پویا)",
    statusDisconnected: "استفاده از حافظه کش محلی / ویدیوهای آفلاین پیش‌فرض",
    btnSaveSync: "ذخیره و همگام‌سازی",
    btnDisconnect: "قطع اتصال و بازگشت به آفلاین",
    guideTitle: "راهنمای همگام‌سازی یوتیوب",
    guideP1: "۱. به کنسول توسعه‌دهندگان گوگل (Google Cloud Console) مراجعه کرده و یک پروژه جدید بسازید.",
    guideP2: "۲. کتابخانه YouTube Data API v3 را جستجو و فعال کنید.",
    guideP3: "۳. در بخش Credentials یک کلید API (API Key) جدید بسازید و آن را در کادر بالا قرار دهید.",
    guideP4: "۴. شناسه کانال خود را وارد کرده و دکمه ذخیره را بزنید. ویدیوهای شما فوراً جایگزین این بخش می‌شوند!",
    
    // Articles and Archetypes
    articlesTitleFeatured: "کتیبه‌های دیجیتال",
    articlesTitleAll: "کتیبه‌های دیجیتال (مقالات)",
    articlesSubtitleFeatured: "Featured Inscriptions",
    articlesSubtitleAll: "The Digital Inscriptions",
    articlesDescFeatured: "جایی برای مطالعه عمیق‌تر؛\nجایی که روایت‌ها به تحلیل تبدیل می‌شوند و اسطوره‌ها از زاویه‌ای تازه خوانده می‌شوند.",
    articlesDescAll: "",
    activeFilter: "فیلتر فعال:",
    readInscription: "خوانش کتیبه",
    readTime: "زمان مطالعه:",
    readTimeSuffix: "مطالعه",
    viewAllArticles: "مشاهده تمامی کتیبه‌ها و مقالات",
    noArticlesFound: "کتیبه‌ای با فیلترهای کنونی در این تالار اسناد یافت نشد.",
    mappedArchetype: "کهن‌الگوی مرتبط: ",
    allArticles: "همه کتیبه‌ها",
    allCategories: "همه دسته‌بندی‌ها",
    
    // Markdown Reader
    transcriptReader: "خوانشگر متون کتیبه",
    fontSizeSm: "فونت کوچک",
    fontSizeBase: "فونت استاندارد",
    fontSizeLg: "فونت بزرگ",
    fontSizeXl: "فونت خیلی بزرگ",
    devBanner: "توسعه‌دهنده گرامی: این مقاله به طور پویا از فایل فیزیکی زیر بارگذاری می‌شود:",
    journalTitle: "دفترچه تاملات شخصی روان‌کاوی",
    journalDesc: "اینجا تاملات شخصی یا یادداشت‌های مربوط به کهن‌الگوهای این کتیبه را وارد کنید. نوشته‌های شما به صورت محلی در مرورگر شما رمزنگاری و ذخیره خواهند شد.",
    journalPlaceholder: "در مورد این اسطوره و ارتباط آن با روان خود بنویسید (به عنوان مثال: سایه‌های ضحاکی زندگی شما چیست؟)",
    journalSave: "ثبت در دفترچه روان",
    journalSavedSuccess: "تاملات شما بر روی این کتیبه با موفقیت ذخیره شد.",
    backToHall: "بازگشت به تالار اصلی",
    
    // Footer
    footerSignature: "مهر یارتان و فرّ ایزدی همراهتان.",
    footerSubtitle: "حکمت باستان برای انسان امروز",
    footerSectionTitle: "بخش‌های پورتال",
    footerSectionHome: "تالار اصلی (صفحه اصلی)",
    footerSectionVideos: "لوح‌های ویدیویی (یوتیوب)",
    footerSectionArticles: "کتیبه‌های دیجیتال (مقالات)",
    footerArchetypesTitle: "کهن‌الگوهای اصلی",
    footerArchSelf: "کهن‌الگوی خود برتر (سیمرغ)",
    footerArchShadow: "کهن‌الگوی سایه درون (ضحاک)",
    footerArchWise: "پیر فرزانه و فر کیانی",
    footerTechTitle: "مستندات و فناوری",
    footerTechReact: "برنامه نوشته شده با React و Vite",
    footerTechTailwind: "استایل‌دهی با Tailwind CSS",
    footerTechMarkdown: "مدیریت محتوا از طریق Markdown فیزیکی",
    footerSocialTitle: "شبکه‌های اجتماعی",
    footerYoutube: "کانال یوتیوب راز و روان",
    footerTelegram: "کانال تلگرام راز و روان",
    footerCredit: "راز و روان — طراحی شده با عشق و فر کیانی",
    footerCopyright: "کلیه حقوق مادی و معنوی متعلق به راز و روان است."
  },
  en: {
    // Header
    brandName: "RazoRavan",
    brandSubtitle: "Secret & Soul",
    navHome: "Main Hall",
    navVideos: "Videos",
    navArticles: "Articles",
    navQuiz: "Self-Test",
    navHaftKhan: "Haft-Khan",
    deepReflectionActive: "Deep Reflection: Active",
    deepReflectionInactive: "Deep Reflection Mode",
    muteSound: "Mute Inner Harmony",
    unmuteSound: "Listen to Inner Harmony (528Hz)",
    
    // Hero
    heroTitle: "The Great Portal of RazoRavan",
    heroSubtitle: "Integrating Ancient Mythical Splendor with the Depth of Carl Jung's Analytical Psychology",
    heroDescription: "This portal is your digital temple to explore the collective unconscious and symbols of Persian national epic. Guided by the Simurgh, ascend beyond Zahhak's shadows to reach self-individuation in harmony with the Divine Aura (Farr).",
    archetypeTitle: "— Inscriptions of Psychical Archetypes —",
    epicAdaptation: "Epic Adaptation:",
    jungianPerspective: "Jungian Perspective:",
    shahnamehSymbol: "Shahnameh Symbol",
    discoverArticles: "Discover Related Articles",
    
    // YouTube Feed
    videoTitle: "Featured YouTube Video Tablets",
    videoSubtitle: "An exquisite collection of audio-visual psychological analyses of Iranian mythology",
    searchVideos: "Search videos...",
    viewAllVideos: "View All Video Tablets",
    duration: "Duration:",
    publishDate: "Published:",
    watchOnYoutube: "To watch this video on YouTube, click on the options on the right:",
    watchOnYoutubeBtn: "Watch on YouTube App",
    shareVideo: "Share Video",
    copyLink: "Copy Link",
    close: "Close",
    linkCopied: "Video link copied to clipboard successfully!",
    
    // YouTube Connection Settings (Admin)
    ytSettings: "YouTube Connection Settings",
    ytSettingsSubtitle: "Admin and custom configuration section (Local storage access)",
    channelIdLabel: "YouTube Channel ID (Username or ID starting with UC):",
    apiKeyLabel: "Google YouTube Data API v3 Key:",
    statusLabel: "Connection Status:",
    statusConnected: "Connected to Live YouTube Channel (Dynamic Display)",
    statusDisconnected: "Using local cache / default offline videos",
    btnSaveSync: "Save & Sync",
    btnDisconnect: "Disconnect & Go Offline",
    guideTitle: "YouTube Sync Guide",
    guideP1: "1. Go to Google Cloud Console and create a new project.",
    guideP2: "2. Search for and enable the 'YouTube Data API v3'.",
    guideP3: "3. Under Credentials, generate a new 'API Key' and paste it in the input above.",
    guideP4: "4. Enter your Channel ID and click Save & Sync. Your live feed will update instantly!",
    
    // Articles and Archetypes
    articlesTitleFeatured: "Featured Inscriptions (Articles)",
    articlesTitleAll: "Written Inscriptions & Articles (Markdown)",
    articlesSubtitleFeatured: "Featured Inscriptions",
    articlesSubtitleAll: "The Digital Inscriptions",
    articlesDescFeatured: "Read selected articles and analyses of archetypes. Use the button below to browse our categorized archives.",
    articlesDescAll: "",
    activeFilter: "Active Filter:",
    readInscription: "Read Inscription",
    readTime: "Read Time:",
    readTimeSuffix: "read",
    viewAllArticles: "View All Inscriptions & Articles",
    noArticlesFound: "No inscriptions were found matching current filters in this hall.",
    mappedArchetype: "Associated Archetype: ",
    allArticles: "All Inscriptions",
    allCategories: "All Categories",
    
    // Markdown Reader
    transcriptReader: "Inscription Transcript Reader",
    fontSizeSm: "Small Font",
    fontSizeBase: "Standard Font",
    fontSizeLg: "Large Font",
    fontSizeXl: "Extra Large Font",
    devBanner: "Developer Note: This article is dynamically loaded from the physical file path:",
    journalTitle: "Personal Psychoanalysis Journal",
    journalDesc: "Record your reflections or thoughts regarding this archetype here. Your notes are stored securely and locally on your browser.",
    journalPlaceholder: "Write about this myth and its connection to your psyche (e.g., What are the Zahhak shadows of your life?)",
    journalSave: "Record in Journal of Soul",
    journalSavedSuccess: "Your reflections on this inscription have been saved successfully.",
    backToHall: "Back to Main Hall",
    
    // Footer
    footerSignature: "\"May Love be your ally, and the Divine Grace your companion.\"",
    footerSubtitle: "Ancient Wisdom for the Modern Soul",
    footerSectionTitle: "Portal Sections",
    footerSectionHome: "Main Hall (Home)",
    footerSectionVideos: "Video Tablets (YouTube)",
    footerSectionArticles: "Written Inscriptions (Articles)",
    footerArchetypesTitle: "Core Archetypes",
    footerArchSelf: "The Higher Self (Simurgh)",
    footerArchShadow: "The Shadow Within (Zahhak)",
    footerArchWise: "The Wise Old Man & Divine Farr",
    footerTechTitle: "Docs & Technologies",
    footerTechReact: "App built with React and Vite",
    footerTechTailwind: "Styled with Tailwind CSS",
    footerTechMarkdown: "Content managed via physical Markdown",
    footerSocialTitle: "Social Networks",
    footerYoutube: "RazoRavan YouTube Channel",
    footerTelegram: "RazoRavan Telegram Channel",
    footerCredit: "RazoRavan — Crafted with Love & Divine Aura",
    footerCopyright: "© All material and intellectual rights belong to RazoRavan."
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function detectLangFromUrl(): Language | null {
  if (typeof window === "undefined") return null;
  const p = window.location.pathname;
  if (p === "/en" || p.startsWith("/en/")) return "en";
  return null;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Language>(() => {
    const fromUrl = detectLangFromUrl();
    if (fromUrl) return fromUrl;
    const saved = localStorage.getItem("RO_LANG");
    return (saved === "fa" || saved === "en") ? saved : "fa";
  });

  // Wrap setLang so a manual toggle also rewrites the URL prefix, keeping the
  // user on a locale-correct path that matches the visible language.
  const setLang = (next: Language) => {
    setLangState(next);
    if (typeof window === "undefined") return;
    const current = window.location.pathname;
    const onEn = current === "/en" || current.startsWith("/en/");
    if (next === "en" && !onEn) {
      window.history.replaceState({}, "", "/en" + (current === "/" ? "" : current));
    } else if (next === "fa" && onEn) {
      const stripped = current.replace(/^\/en(?=\/|$)/, "") || "/";
      window.history.replaceState({}, "", stripped);
    }
  };

  useEffect(() => {
    localStorage.setItem("RO_LANG", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "fa" ? "rtl" : "ltr";
  }, [lang]);

  const isRtl = lang === "fa";

  const localized = (path: string) => {
    const p = path.startsWith("/") ? path : `/${path}`;
    if (lang === "en") return p === "/" ? "/en" : `/en${p}`;
    return p;
  };

  const t = (key: keyof typeof translations["fa"]) => {
    const val = translations[lang][key];
    if (val !== undefined) return val;
    const faVal = translations["fa"][key];
    if (faVal !== undefined) return faVal;
    return String(key);
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, isRtl, t, localized }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
